import React, { useEffect, useState } from "react";
import Editor from "@monaco-editor/react";
import { toast } from "sonner";
import { Link2, FileText, Play, Loader2, Terminal, MessageSquare, Send, Trash2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const MONACO_LANG = { python: "python", javascript: "javascript" };

export function LessonBlockView({ data }) {
  return (
    <div className="glass-panel rounded-2xl p-6 mb-6" data-testid="rendered-block-lesson">
      {(data.content || "").split("\n\n").map((para, i) => (
        <p key={i} className="text-sm text-slate-300 leading-relaxed mb-3 last:mb-0">{para}</p>
      ))}
      {data.video_url && (
        <a href={data.video_url} target="_blank" rel="noreferrer" data-testid="lesson-block-video-link"
          className="inline-flex items-center gap-2 text-xs font-semibold text-[#5C9DFF] hover:text-[#7fb4ff] mt-3">
          <Link2 className="w-3.5 h-3.5" /> Watch embedded video
        </a>
      )}
    </div>
  );
}

export function ResourcesBlockView({ data }) {
  return (
    <div className="glass-panel rounded-2xl p-6 mb-6" data-testid="rendered-block-resources">
      <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Resources</p>
      <div className="space-y-2">
        {(data.items || []).map((it, i) => (
          <a key={i} href={it.url} target="_blank" rel="noreferrer" data-testid={`resource-link-${i}`}
            className="flex items-center gap-2 text-sm text-[#5C9DFF] hover:text-[#7fb4ff]">
            <Link2 className="w-3.5 h-3.5" /> {it.label || it.url}
          </a>
        ))}
      </div>
    </div>
  );
}

export function ProjectBlockView({ data }) {
  return (
    <div className="glass-panel rounded-2xl p-6 mb-6" data-testid="rendered-block-mini_project">
      <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Mini-Project</p>
      <p className="text-sm text-slate-300 leading-relaxed">{data.description}</p>
      <span className="inline-block mt-2 text-[10px] font-bold uppercase text-amber-300 bg-amber-400/10 px-2 py-0.5 rounded-full">Submit: {data.submission_type}</span>
    </div>
  );
}

export function CodePlaygroundBlockView({ data, challengeId, day }) {
  const [code, setCode] = useState(data.starter_code || "");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setRunning(true);
    try {
      const { data: res } = await api.post(`/custom-challenges/${challengeId}/day/${day}/run-code`, { language: data.language, code });
      setResult(res);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to run code");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="glass-panel rounded-2xl p-6 mb-6" data-testid="rendered-block-code_playground">
      <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1.5"><Terminal className="w-3.5 h-3.5" /> Code Playground · {data.language === "javascript" ? "JavaScript" : "Python"}</p>
      {data.description && <p className="text-sm text-slate-300 mb-3">{data.description}</p>}
      <div className="rounded-xl overflow-hidden border border-white/10">
        <Editor
          height="220px" language={MONACO_LANG[data.language] || "python"} theme="vs-dark" value={code}
          onChange={(v) => setCode(v || "")} options={{ minimap: { enabled: false }, fontSize: 13, scrollBeyondLastLine: false }}
          data-testid="code-playground-editor"
        />
      </div>
      <button onClick={run} disabled={running} data-testid="code-playground-run-btn"
        className="flex items-center gap-2 mt-3 bg-primary hover:bg-primary/90 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-lg">
        {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Run
      </button>
      {result && (
        <pre data-testid="code-playground-output" className="mt-3 text-xs font-mono text-slate-300 bg-black/40 border border-white/10 rounded-lg p-3 whitespace-pre-wrap max-h-48 overflow-y-auto">
          {result.stdout}{result.stderr && <span className="text-rose-400">{result.stderr}</span>}
          {result.timed_out && <span className="text-amber-400">Time Limit Exceeded</span>}
          {!result.stdout && !result.stderr && !result.timed_out && "(no output)"}
        </pre>
      )}
    </div>
  );
}

export function DiscussionBlockView({ data, challengeId, day, interactive = true, creatorUserId }) {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [message, setMessage] = useState("");
  const [posting, setPosting] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data: res } = await api.get(`/custom-challenges/${challengeId}/day/${day}/discussion`);
      setPosts(res);
    } catch (e) {
      // silent — discussion is a nice-to-have, don't block the day
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [challengeId, day]);

  const post = async () => {
    if (!message.trim()) return;
    setPosting(true);
    try {
      await api.post(`/custom-challenges/${challengeId}/day/${day}/discussion`, { message });
      setMessage("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to post");
    } finally {
      setPosting(false);
    }
  };

  const remove = async (postId) => {
    try {
      await api.delete(`/custom-challenges/${challengeId}/day/${day}/discussion/${postId}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to remove post");
    }
  };

  return (
    <div className="glass-panel rounded-2xl p-6 mb-6" data-testid="rendered-block-discussion">
      <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1.5"><MessageSquare className="w-3.5 h-3.5" /> Discussion</p>
      <p className="text-sm text-slate-300 mb-3">{data.prompt}</p>
      {interactive && (
        <div className="flex items-center gap-2 mb-4">
          <input value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Share your answer..." data-testid="discussion-message-input"
            className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 outline-none focus:border-indigo-400/50" />
          <button onClick={post} disabled={posting || !message.trim()} data-testid="discussion-post-btn"
            className="flex items-center gap-1.5 bg-primary hover:bg-primary/90 disabled:opacity-50 text-white text-sm font-semibold px-3 py-2 rounded-lg">
            {posting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          </button>
        </div>
      )}
      {!loading && (
        <div className="space-y-3" data-testid="discussion-posts-list">
          {posts.length === 0 && <p className="text-xs text-slate-500">No answers yet — be the first to share.</p>}
          {posts.map((p) => (
            <div key={p.id} className="flex items-start justify-between gap-3 border-t border-white/5 pt-3" data-testid={`discussion-post-${p.id}`}>
              <div>
                <p className="text-xs font-semibold text-white">{p.user_name}</p>
                <p className="text-sm text-slate-300 mt-0.5">{p.message}</p>
              </div>
              {(p.user_id === user?.user_id || (creatorUserId && creatorUserId === user?.user_id)) && (
                <button onClick={() => remove(p.id)} data-testid={`discussion-delete-btn-${p.id}`} className="text-slate-500 hover:text-red-400 shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function ChallengeBlockList({ blocks, challengeId, day, interactive = true, creatorUserId }) {
  return (
    <>
      {(blocks || []).filter((b) => b.type !== "quiz").map((b) => {
        if (b.type === "lesson") return <LessonBlockView key={b.id} data={b.data} />;
        if (b.type === "resources") return <ResourcesBlockView key={b.id} data={b.data} />;
        if (b.type === "mini_project") return <ProjectBlockView key={b.id} data={b.data} />;
        if (b.type === "code_playground") return <CodePlaygroundBlockView key={b.id} data={b.data} challengeId={challengeId} day={day} />;
        if (b.type === "discussion") return <DiscussionBlockView key={b.id} data={b.data} challengeId={challengeId} day={day} interactive={interactive} creatorUserId={creatorUserId} />;
        return null;
      })}
    </>
  );
}
